import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { FaClipboardCheck, FaPencilAlt, FaSignOutAlt, FaPlus, FaToggleOn, FaToggleOff, FaSearch } from "react-icons/fa";
import API_HOST, { HEADERS_JSON, METHOD_POST } from "../../../ApiConfig";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ConfirmModal from "../../../components/ConfirmModal";
import useDigitalTheme from "../../hooks/useDigitalTheme";
import BarraMenu from "../../../components/BarraMenu";
import PageHero from "../../../components/PageHero";

const PER_PAGE    = 10;
const MIN_CHARS   = 3;
const DEBOUNCE_MS = 400;

// ── Helpers ──────────────────────────────────────────────────────────────────
const parseSafeJson = (text: string): any | null => {
  try { return JSON.parse(text); } catch { return null; }
};

const apiPost = async (host: string, body: object): Promise<any | null> => {
  try {
    const res = await fetch(host, { method: METHOD_POST, headers: HEADERS_JSON, body: JSON.stringify(body) });
    return parseSafeJson(await res.text());
  } catch { return null; }
};

const buildQuestionPayload = (questionEdit: any, modalMode: string, modalGroup: string, selectedGroup: string, emp: any): any => ({
  valid: modalMode === "add" ? "newQuestion" : "editQuestion",
  category_id: questionEdit.categoria,
  title: questionEdit.titulo,
  description: questionEdit.pregunta,
  evaluation_phase: questionEdit.fase,
  state_type: questionEdit.states === "activo" ? 1 : 0,
  minimum: 0, maximum: 0, type: "",
  user_id: emp?.id_user ?? null,
  idbusiness_group: modalGroup || selectedGroup,
});

const resolveGroupSelection = (isEvaluador: boolean, evaluatorGroup: string, activeGroups: any[]): string => {
  if (isEvaluador && evaluatorGroup) return evaluatorGroup;
  return activeGroups.length > 0 ? String(activeGroups[0].idbusiness_group) : "";
};

const gfeColumns = (
  handleChangeStatus: (id: number, s: string) => void,
  setModalMode: any, setQuestionEdit: any, setModalGroup: any, setShowModal: any,
  selectedGroup: string
) => [
  { name: "Fase", selector: (row: any) => row.evaluation_phase_description || "", sortable: true, width: "140px", wrap: true },
  { name: "Categoría", selector: (row: any) => row.categoria || "", sortable: true, width: "180px", wrap: true },
  { name: "Título", selector: (row: any) => row.title || "", sortable: true, width: "200px", wrap: true },
  { name: "Descripción", selector: (row: any) => row.description || "", sortable: true, width: "300px", wrap: true },
  { name: "Estado", sortable: true, width: "110px",
    cell: (row: any) => (
      <span className={`badge ${row.states === "activo" ? "" : "bg-secondary"}`}
        style={{ fontSize: "12px", padding: "6px 10px", backgroundColor: row.states === "activo" ? "#151D20" : undefined }}>
        {row.states === "activo" ? "Activo" : "Inactivo"}
      </span>
    ) },
  { name: "Acciones", ignoreRowClick: true, allowOverflow: true, button: true, width: "90px",
    cell: (row: any) => (
      <div className="d-flex gap-2 justify-content-center align-items-center">
        <button className="btn btn-sm p-0" title="Editar"
          onClick={() => { setModalMode("edit"); setQuestionEdit({ id: row.id, categoria: row.category_id, titulo: row.title, pregunta: row.description, fase: row.evaluation_phase, states: row.states }); setModalGroup(selectedGroup); setShowModal(true); }}
          style={{ background: "none", border: "none" }}>
          <FaPencilAlt color="white" className="bg-warning rounded-circle p-1" style={{ fontSize: "26px" }} />
        </button>
        <button className="btn btn-sm p-0" title={row.states === "activo" ? "Inactivar" : "Activar"}
          onClick={() => handleChangeStatus(row.id, row.states)}
          style={{ background: "none", border: "none" }}>
          {row.states === "activo"
            ? <FaToggleOn  color="white" className="bg-success rounded-circle p-1" style={{ fontSize: "26px" }} />
            : <FaToggleOff color="white" className="bg-secondary rounded-circle p-1" style={{ fontSize: "26px" }} />}
        </button>
      </div>
    ) },
];

const FormatoEvaluacionPage = () => {
  const navigate    = useNavigate();
  const emp: any    = useSelector((state: any) => state.emp);
  const isEvaluador = emp?.profile === "Evaluador";
  const evaluatorGroup = emp?.idbusiness_group ? String(emp.idbusiness_group) : "";

  const [businessGroups, setBusinessGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup]   = useState("");
  const [questions, setQuestions]           = useState<any[]>([]);
  const [loading, setLoading]               = useState(false);

  // Búsqueda server-side
  const [searchInput, setSearchInput]     = useState("");
  const [searchActive, setSearchActive]   = useState("");
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalRows, setTotalRows]         = useState(0);

  // Modal crear/editar
  const [showModal, setShowModal]   = useState(false);
  const [modalMode, setModalMode]   = useState<"add"|"edit">("add");
  const [questionEdit, setQuestionEdit] = useState<any>({ id: null, categoria: "", titulo: "", pregunta: "", fase: "", states: "activo" });
  const [categories, setCategories] = useState<any[]>([]);
  const [phases, setPhases]         = useState<any[]>([]);
  const [modalGroup, setModalGroup] = useState("");
  const [clients, setClients]       = useState<any[]>([]);
  const [modalClient, setModalClient] = useState("");
  const [savingMsg, setSavingMsg]   = useState("");
   
  useDigitalTheme();

  // Modal confirmación (reemplaza window.confirm y alert)
  const [confirm, setConfirm] = useState({ show: false, mensaje: "", variante: "danger" as "danger"|"warning", accion: () => {} });
  const pedirConfirm = (msg: string, fn: () => void, v: "danger"|"warning" = "warning") =>
    setConfirm({ show: true, mensaje: msg, variante: v, accion: fn });
  const cerrarConfirm = () => setConfirm(c => ({ ...c, show: false }));

  // Modal info (reemplaza alert)
  const [infoMsg, setInfoMsg] = useState("");

  // ── Carga paginada ──────────────────────────────────────────────────────
  const fetchQuestions = async (page = 1, search = "") => {
    if (!selectedGroup) return;
    setLoading(true);
    const trimmed = search.trim();
    const usePaged = trimmed.length >= MIN_CHARS || page > 1;
    const body: any = usePaged
      ? { valid: "selectAllQuestionPaged", idbusiness_group: selectedGroup, search: trimmed, page, per_page: PER_PAGE }
      : { valid: "selectAllQuestion", idbusiness_group: selectedGroup };
    const raw = await apiPost(API_HOST, body);
    setQuestions(raw?.payload || []);
    setTotalRows(raw?.total ?? (raw?.payload?.length ?? 0));
    setCurrentPage(page);
    setLoading(false);
  };

  const handlePageChange = (page: number) => fetchQuestions(page, searchActive);

  // ── Debounce búsqueda ───────────────────────────────────────────────────
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchActive) return;
    if (trimmed.length > 0 && trimmed.length < MIN_CHARS) return;
    const timer = setTimeout(() => {
      setSearchActive(trimmed);
      fetchQuestions(1, trimmed);
    }, trimmed.length === 0 ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ── Carga grupos de negocio ─────────────────────────────────────────────
  const fetchBusinessGroups = async () => {
    const data = await apiPost(API_HOST, { valid: "getSelectCompany" });
    if (!data || !Array.isArray(data.payload)) { setBusinessGroups([]); return; }
    const active = data.payload.filter((item: any) => item.status === "activo");
    setBusinessGroups(active);
    const resolved = resolveGroupSelection(isEvaluador, evaluatorGroup, active);
    if (resolved) setSelectedGroup(resolved);
  };

  const fetchClients    = async () => { const d = await apiPost(API_HOST, { valid: "getSelectClientsWithGroup" }); if (d?.payload) setClients(d.payload); };
  const fetchCategories = async (gId: string) => { const d = await apiPost(API_HOST, { valid: "selectCategoriesByGroup", idbusiness_group: gId }); setCategories(d?.payload || []); };
  const fetchPhases     = async () => { const d = await apiPost(API_HOST, { valid: "getEvaluationPhases" }); setPhases(d?.payload || []); };

  useEffect(() => { fetchBusinessGroups(); }, []);
  useEffect(() => { if (isEvaluador && evaluatorGroup) setSelectedGroup(evaluatorGroup); }, [isEvaluador, evaluatorGroup]);
  useEffect(() => { if (selectedGroup) { setSearchInput(""); setSearchActive(""); fetchQuestions(1, ""); } }, [selectedGroup]);
  useEffect(() => { if (showModal && (modalGroup || selectedGroup)) fetchCategories(modalGroup || selectedGroup); if (showModal) { fetchPhases(); fetchClients(); } }, [showModal, modalGroup, selectedGroup]);

  // ── Guardar pregunta ────────────────────────────────────────────────────
  const handleSaveQuestion = async () => {
    try {
      const payload: any = buildQuestionPayload(questionEdit, modalMode, modalGroup, selectedGroup, emp);
      if (modalMode === "edit" && questionEdit.id) payload.id = questionEdit.id;
      const data = await apiPost(API_HOST, payload);
      if (data?.code === 1) {
        setSavingMsg(data.message || "Pregunta guardada exitosamente");
        setShowModal(false);
        fetchQuestions(currentPage, searchActive);
      } else {
        setInfoMsg(data?.message || "Error al guardar la pregunta");
      }
    } catch { setInfoMsg("Error de conexión al guardar la pregunta"); }
  };

  // ── Cambiar estado ──────────────────────────────────────────────────────
  const handleChangeStatus = (id: number, currentStates: string) => {
    const newStatus = currentStates === "activo" ? "inactivo" : "activo";
    pedirConfirm(
      `¿Seguro que desea cambiar el estado de esta pregunta a ${newStatus}?`,
      async () => {
        setLoading(true);
        const data = await apiPost(API_HOST, { valid: "changeQuestionStatus", id, states: newStatus });
        if (data?.code === 1) fetchQuestions(currentPage, searchActive);
        else setInfoMsg(data?.message || "Error al cambiar el estado");
        setLoading(false);
      }
    );
  };

  const columns = gfeColumns(handleChangeStatus, setModalMode, setQuestionEdit, setModalGroup, setShowModal, selectedGroup);

  const pagOptions = { rowsPerPageText: "Filas por páginas", rangeSeparatorText: "de", selectAllRowsItem: true, selectAllRowsItemText: "Todos" };

  return (
  <div className="container bg-white gestion-colaboradores-container page-shell">
    <BarraMenu />
    <div className="page-shell__body">
    <PageHero
      icon={<FaClipboardCheck />}
      title="Formato de Evaluación"
      subtitle="Gestión de preguntas y estructura del formato por grupo de negocio"
      badge={
        <div className="dashboard-hero__badge">
          {questions.length} preguntas
        </div>
      }
    />

      <div className="page-shell__toolbar">
        <div className="page-shell__toolbar-grow">
          <label htmlFor="businessGroupSelect" className="form-label">Panel del cliente:</label>
          <select id="businessGroupSelect" className="form-select" value={selectedGroup}
            onChange={e => setSelectedGroup(e.target.value)} disabled={isEvaluador}>
            {!selectedGroup && <option value="">Seleccione un grupo...</option>}
            {businessGroups.map((g: any) => (
              <option key={g.idbusiness_group} value={g.idbusiness_group}>{g.name}</option>
            ))}
          </select>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}><FaSignOutAlt /> Regresar</Button>
        <Button variant="success" onClick={() => { setModalMode("add"); setQuestionEdit({ id: null, categoria: "", titulo: "", pregunta: "", fase: "", states: "activo" }); setModalGroup(selectedGroup); setModalClient(""); fetchCategories(selectedGroup); setShowModal(true); }}>
          <FaPlus /> Nueva Pregunta
        </Button>
      </div>

      {/* Buscador */}
      <div className="mb-2" style={{ position: "relative", maxWidth: "400px" }}>
        <div className="input-group">
          <span className="input-group-text bg-white"><FaSearch size={13} color="#888" /></span>
          <input type="text" className="form-control"
            placeholder="Buscar por título, descripción o categoría (mín. 3 car.)..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)} />
          {searchInput && (
            <button className="btn btn-outline-secondary" type="button"
              onClick={() => { setSearchInput(""); }}>✕</button>
          )}
        </div>
        {searchInput.length > 0 && searchInput.trim().length < MIN_CHARS && (
          <small className="text-muted" style={{ position: "absolute", bottom: "-17px", left: "4px", fontSize: "11px" }}>
            Escribe al menos {MIN_CHARS} caracteres
          </small>
        )}
      </div>

      <div className="page-shell__table-wrap mt-2">
        <DataTable
          columns={columns}
          data={questions}
          progressPending={loading}
          pagination
          paginationServer={searchInput.trim().length >= MIN_CHARS}
          paginationTotalRows={searchInput.trim().length >= MIN_CHARS ? totalRows : undefined}
          onChangePage={handlePageChange}
          paginationPerPage={PER_PAGE}
          paginationDefaultPage={currentPage}
          highlightOnHover
          paginationComponentOptions={pagOptions}
          progressComponent={<div className="p-3 text-muted">Cargando preguntas...</div>}
          noDataComponent={<div className="p-4 text-muted">No hay preguntas para este grupo</div>}
        />
      </div>

      {/* Modal crear/editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === "add" ? "Nueva Pregunta" : "Editar Pregunta"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savingMsg && <div className="alert alert-success py-2">{savingMsg}</div>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Grupo de Negocio</Form.Label>
              <Form.Select value={modalGroup} onChange={e => { setModalGroup(e.target.value); fetchCategories(e.target.value); }}>
                <option value="">Seleccione un grupo...</option>
                {businessGroups.map((g: any) => <option key={g.idbusiness_group} value={g.idbusiness_group}>{g.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cliente (opcional)</Form.Label>
              <Form.Select value={modalClient} onChange={e => { const id = e.target.value; setModalClient(id); if (id) { const found = clients.find((c: any) => String(c.id) === id); if (found?.idbusiness_group) { setModalGroup(String(found.idbusiness_group)); fetchCategories(String(found.idbusiness_group)); } } }}>
                <option value="">Todos los clientes</option>
                {clients.map((c: any) => <option key={c.id} value={c.id}>{c.description}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categoría</Form.Label>
              <Form.Select value={questionEdit.categoria} onChange={e => setQuestionEdit({ ...questionEdit, categoria: e.target.value })} disabled={!categories.length}>
                <option value="">Seleccione una categoría</option>
                {categories.map((cat: any) => <option key={cat.id} value={cat.id}>{cat.description}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Título</Form.Label>
              <Form.Control type="text" value={questionEdit.titulo} onChange={e => setQuestionEdit({ ...questionEdit, titulo: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Pregunta</Form.Label>
              <Form.Control type="text" value={questionEdit.pregunta} onChange={e => setQuestionEdit({ ...questionEdit, pregunta: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fase de Evaluación</Form.Label>
              <Form.Select value={questionEdit.fase} onChange={e => setQuestionEdit({ ...questionEdit, fase: e.target.value })} disabled={!phases.length}>
                <option value="">Seleccione una fase</option>
                {phases.map((p: any) => <option key={p.id} value={p.id}>{p.description}</option>)}
              </Form.Select>
            </Form.Group>
            {modalMode === "edit" && (
              <Form.Group className="mb-3">
                <Form.Label>Estado</Form.Label>
                <Form.Select value={questionEdit.states} onChange={e => setQuestionEdit({ ...questionEdit, states: e.target.value })}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </Form.Select>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveQuestion}
            disabled={!(questionEdit.categoria && questionEdit.titulo && questionEdit.pregunta && questionEdit.fase && (modalGroup || selectedGroup))}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal info (reemplaza alert) */}
      <Modal show={!!infoMsg} onHide={() => setInfoMsg("")} centered size="sm">
        <Modal.Header closeButton><Modal.Title style={{ fontSize: "15px" }}>Aviso</Modal.Title></Modal.Header>
        <Modal.Body><p style={{ margin: 0 }}>{infoMsg}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="primary" size="sm" onClick={() => setInfoMsg("")}>Aceptar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal confirmación (reemplaza window.confirm) */}
      <ConfirmModal show={confirm.show} mensaje={confirm.mensaje} variante={confirm.variante}
        onConfirm={() => { cerrarConfirm(); confirm.accion(); }} onCancel={cerrarConfirm} />
    </div>
    </div>
  );
};

export default FormatoEvaluacionPage;